import * as React from "react"
import styled from "styled-components"
import { Icon } from ".."
import { breakpoint, ColorKeys, colors, scale } from "../../styles"
import { Rte } from "../Rte"
import { Text } from "../Text"

interface CreateMarkup {
	__html: string
}

export const createMarkup = (markup: string): CreateMarkup => ({
	__html: markup,
})

export interface InnerStyle {
	backgroundColor?: ColorKeys
}

export interface MediaContentRteProps {
	text: string
}

export interface MediaContentComponent {
	Title: React.FC
	RteContent: React.FC<MediaContentRteProps>
}

export interface MediaContentStyledProps {
	mirror?: boolean
	innerStyle?: InnerStyle
	children?: React.ReactNode
}
export interface MediaContentProps extends MediaContentStyledProps {
	media: React.ReactNode
	cta?: React.ReactNode
}

const Media = styled.figure`
	position: relative;
	flex: 0 0 50%;

	&:after {
		content: "";
		display: block;
		padding-bottom: 60.68%;
	}

	img {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
	}

	${breakpoint("m")`
        &:after {
            display: none;
        }
    `}
`

const Inner = styled.div<InnerStyle>`
	width: 100%;
	padding: ${scale.px(4)} ${scale.px(3)};
	background-color: ${({ backgroundColor }) =>
		backgroundColor ?? colors.greyF3};

	${breakpoint("m")`
		padding: ${scale.px(6.5)};
    `}

	${breakpoint("l")`
		padding: ${scale.px(6.5)} ${scale.px(11.5)};
    `}

  ${breakpoint("xl")`
		padding: ${scale.px(6.5)} ${scale.px(13.5)};
    `}
`

const RteWrapper = styled.div`
	position: relative;
	font-size: ${scale.px(2)};
	line-height: ${scale.px(3)};
	margin-bottom: ${scale.px(2)};
`

const MediaContentStyled = styled.div<MediaContentStyledProps>`
	position: relative;
	display: block;
	width: 100%;
	border-radius: ${scale.px(3)};
	overflow: hidden;

	${breakpoint("m")<MediaContentStyledProps>`
		display: flex;
        flex-direction: ${(props) => (props.mirror ? "row-reverse" : "row")};
		min-height: ${scale.px(43)};
    `}

	svg {
		display: none;
	}
	a & {
		svg {
			display: block;
			position: absolute;
			top: 50%;
			right: ${scale.px(3)};
		}
		border: 1px solid ${colors.blue33};
	}
	&:hover {
		border-color: ${colors.hoverBlue};
		& svg {
			path {
				fill: ${colors};
			}
		}
	}
`

const MediaContent: React.FC<MediaContentProps> & MediaContentComponent = ({
	media,
	cta,
	children,
	innerStyle,
	...rest
}) => {
	return (
		<MediaContentStyled {...rest}>
			<Media>{media}</Media>
			<Inner {...innerStyle}>
				{children}
				{cta && cta}
				<Icon icon="arrow-right"></Icon>
			</Inner>
		</MediaContentStyled>
	)
}

export interface MediaCompoundProps {
	children?: React.ReactNode
}

const Title: React.FC<MediaCompoundProps> = (props) => (
	<Text as="h3" fontSize={2.5} lineHeight={3} fontWeight="bold">
		{props.children}
	</Text>
)

MediaContent.Title = Title

const RteContent: React.FC<MediaContentRteProps> = (props) => {
	return (
		<RteWrapper>
			<Rte>
				<div dangerouslySetInnerHTML={createMarkup(props.text)}></div>
			</Rte>
		</RteWrapper>
	)
}

MediaContent.RteContent = RteContent

MediaContent.defaultProps = {
	mirror: false,
}

export { MediaContent }
